import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { MONTHLY_REPORT_URL } from "../../redux/api/config";
import { useDispatch } from "react-redux";
import { setErrorMessage } from "../../redux/actions/appActions";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import { monthYearComparator } from "./sortComparators";
import { exportToExcel } from "./reportUtils";
import { buildClientReportPayload } from "../../redux/actions/b2bActions";
import ClientReportFilters from "./ClientReportFilters";
import React from "react";

interface MonthlyReportData {
  month_year: string;
  total_active_users?: number;
  /** Alternate API name for per-month active users */
  monthly_active_users?: number;
  total_new_users: number;
  education_completed: number;
  preference_completed: number;
  experience_completed: number;
  /** API field for profile completion counts */
  profile_complete?: number;
  profile_completed?: number;
  video_approved: number;
  video_uploaded: number;
  /** Primary API field for Specialisation Completed column (monthly counts). */
  specialisation_status?: number;
  specialisation_completed?: number;
  specialization_completed?: number;
  /** Psychometrics / exam completion counts for the month. */
  exam_completed?: number;
  psychometrics_completed?: number;
  psychometrics?: number;
  /** Normalized for grid / export (set when mapping API rows). */
  specialisation_completed_display?: number;
  psychometrics_display?: number;
}

function pickTotalUsersFromResult(result: any): number | null {
  const v =
    result?.total_users ??
    result?.totalUsers ??
    result?.total_user ??
    result?.totalUser ??
    result?.total_user_count;
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const MonthlyReport = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<MonthlyReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [last30DaysActiveUsers, setLast30DaysActiveUsers] = useState<number | null>(null);
  const [totalUsersDisplay, setTotalUsersDisplay] = useState<number | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const skipNextEffectRef = React.useRef(false);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(true);

  // Sort by month_year descending (newest first)
  const sortByMonthDesc = (records: MonthlyReportData[]) =>
    [...(records || [])].sort((a, b) => monthYearComparator(b.month_year, a.month_year));

  const normalizeMonthlyRow = (record: MonthlyReportData): MonthlyReportData => {
    const specialisation =
      record.specialisation_status ??
      record.specialisation_completed ??
      record.specialization_completed ??
      0;
    const psychometrics =
      record.exam_completed ?? record.psychometrics_completed ?? record.psychometrics ?? 0;
    return {
      ...record,
      total_active_users: record.total_active_users ?? record.monthly_active_users ?? 0,
      profile_completed: record.profile_complete ?? record.profile_completed ?? 0,
      specialisation_completed_display: specialisation,
      psychometrics_display: psychometrics,
    };
  };

  const columns: GridColDef[] = [
    {
      field: "month_year",
      headerName: "Month",
      flex: 1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      sortComparator: monthYearComparator,
    },
    {
      field: "total_active_users",
      headerName: "Monthly Active Users",
      flex: 1,
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "total_new_users",
      headerName: "Total New Users",
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "education_completed",
      headerName: "Education Completed",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "preference_completed",
      headerName: "Preference Completed",
      flex: 1,
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "experience_completed",
      headerName: "Experience Completed",
      flex: 1,
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "profile_completed",
      headerName: "Profile Completed",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "video_approved",
      headerName: "Video Approved",
      flex: 1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "video_uploaded",
      headerName: "Video Uploaded",
      flex: 1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "specialisation_completed_display",
      headerName: "Specialisation Completed",
      flex: 1,
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "psychometrics_display",
      headerName: "Psychometrics",
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
  ];

  const fetchMonthlyReport = async (
    page: number,
    fetchSize: number,
    filtersOverride?: { startDate: string | null; endDate: string | null; year: string; months: number[] }
  ) => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const useStart = filtersOverride?.startDate ?? startDate;
    const useEnd = filtersOverride?.endDate ?? endDate;
    const useYear = filtersOverride?.year ?? selectedYear;
    const useMonths = filtersOverride?.months ?? selectedMonths;

    setLoading(true);
    setData([]); 
    try {
      const payload = buildClientReportPayload(
        {
          startDate: useStart,
          endDate: useEnd,
          year: useYear,
          months: useMonths.length > 0 ? useMonths : undefined,
        },
        false,
        page,
        fetchSize
      );

      const response = await API_MIDDLEWARE(
        MONTHLY_REPORT_URL,
        "POST",
        { "Content-Type": "application/json" },
        payload,
        undefined,
        true,
        true,
        abortControllerRef.current.signal
      );

      const result = response?.result || {};
      const { records, totalRecords, last30DaysActiveUsers: apiLast30 } = result;

      // Sort newest month first, then add unique IDs for DataGrid
      const sortedRecords = sortByMonthDesc(records || []);
      const dataWithIds = sortedRecords.map((record: MonthlyReportData, index: number) => ({
        ...normalizeMonthlyRow(record),
        id: `${record.month_year}-${index}`,
      }));

      const apiTotalUsers = pickTotalUsersFromResult(result);
      const sumNewUsersInRows = sortedRecords.reduce(
        (sum: number, row: MonthlyReportData) => sum + (Number(row.total_new_users) || 0),
        0
      );
      if (apiTotalUsers !== null) {
        setTotalUsersDisplay(apiTotalUsers);
      } else if (sortedRecords.length > 0) {
        setTotalUsersDisplay(sumNewUsersInRows);
      } else {
        setTotalUsersDisplay(null);
      }

      setLoading(false);
      setData(dataWithIds);
      setTotalRecords(totalRecords || 0);
      setLast30DaysActiveUsers(apiLast30 !== undefined && apiLast30 !== null ? Number(apiLast30) : null);
    } catch (err: any) {
      // Don't show error for aborted requests
      if (err.code === 20 || err.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }
      if (err.code !== 401) {
        setLoading(false);
        setTotalUsersDisplay(null);
        dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      }
    } 
  };

  const handleSubmitFilters = () => {
    skipNextEffectRef.current = true;
    setHasSubmitted(true);
    setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
    fetchMonthlyReport(0, paginationModel.pageSize, {
      startDate,
      endDate,
      year: selectedYear,
      months: selectedMonths,
    });
  };

  const handleClearFilters = () => {
    skipNextEffectRef.current = true;
    setStartDate(null);
    setEndDate(null);
    setSelectedYear("");
    setSelectedMonths([]);
    setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
    fetchMonthlyReport(0, paginationModel.pageSize, {
      startDate: null,
      endDate: null,
      year: "",
      months: [],
    });
  };

  const fetchMonthlyReportForDownload = async () => {
    setDownloadingExcel(true);
    try {
      const payload = buildClientReportPayload(
        {
          startDate,
          endDate,
          year: selectedYear,
          months: selectedMonths.length > 0 ? selectedMonths : undefined,
        },
        true
      );

      const response = await API_MIDDLEWARE(
        MONTHLY_REPORT_URL,
        "POST",
        { "Content-Type": "application/json" },
        payload,
        undefined,
        true,
        true
      );

      const { records } = response?.result || {};

      if (!records || records.length === 0) {
        dispatch(setErrorMessage("No data available to download"));
        setDownloadingExcel(false);
        return;
      }

      const sortedRecords = sortByMonthDesc(records);
      const exportData = sortedRecords.map((row: MonthlyReportData) => {
        const n = normalizeMonthlyRow(row);
        return {
          Month: n.month_year,
          "Monthly Active Users": n.total_active_users,
          "Total New Users": n.total_new_users,
          "Education Completed": n.education_completed,
          "Preference Completed": n.preference_completed,
          "Experience Completed": n.experience_completed,
          "Profile Completed": n.profile_completed,
          "Video Approved": n.video_approved,
          "Video Uploaded": n.video_uploaded,
          "Specialisation Completed": n.specialisation_completed_display,
          Psychometrics: n.psychometrics_display,
        };
      });

      exportToExcel({
        data: exportData,
        filename: "Monthly_User_Activity_Report",
        sheetName: "Monthly Report",
        maxColumnWidth: 20,
      });

      setDownloadingExcel(false);
    } catch (err: any) {
      if (err.code !== 401) {
        dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      }
      setDownloadingExcel(false);
    }
  };

  const handleDownloadExcel = () => {
    fetchMonthlyReportForDownload();
  };

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }
    if (hasSubmitted) {
      fetchMonthlyReport(paginationModel.page, paginationModel.pageSize);
    }
  }, [hasSubmitted, paginationModel.page, paginationModel.pageSize]);

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
      {/* Header with Title and Download Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mb: 3,
          pr: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: "600",
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            Monthly User Activity Report
          </Typography>
          <Button
          startIcon={!downloadingExcel ? <DownloadIcon /> : undefined}
          variant="contained"
          disabled={data.length === 0 || loading || downloadingExcel}
          onClick={handleDownloadExcel}
          sx={{
            backgroundColor: "#756DE9",
            color: "#fff",
            borderRadius: "100px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: "#5a4dbf",
            },
            "&:disabled": {
              backgroundColor: "#E0E0E0",
              color: "#999999",
            },
            textTransform: "capitalize",
            fontSize: "0.95rem",
            whiteSpace: "nowrap",
            flexShrink: 0,
            minWidth: "fit-content",
          }}
        >
          {downloadingExcel ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#fff" }} />
              Downloading...
            </Box>
          ) : (
            "Download Excel"
          )}
        </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { md: "stretch" },
          mb: 3,
          pr: 2,
        }}
      >
        <ClientReportFilters
          startDate={startDate}
          endDate={endDate}
          selectedYear={selectedYear}
          selectedMonths={selectedMonths}
          hasSubmitted={hasSubmitted}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onYearChange={setSelectedYear}
          onMonthsChange={setSelectedMonths}
          onSubmit={handleSubmitFilters}
          onClearFilters={handleClearFilters}
          sx={{ mb: 0, flex: 1, minWidth: 0 }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: { xs: "100%", md: "auto" },
            minWidth: { md: 200 },
            maxWidth: { md: 280 },
            flexShrink: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "12px",
              border: "1px solid #E0E0E0",
              backgroundColor: "#fff",
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Total users
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1F2235", mt: 0.5, lineHeight: 1.2 }}>
              {loading ? "—" : totalUsersDisplay !== null ? totalUsersDisplay.toLocaleString() : "—"}
            </Typography>
          </Paper>

          {last30DaysActiveUsers !== null ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#fff",
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Last 30 days active users
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#756DE9", mt: 0.5, lineHeight: 1.2 }}>
                {last30DaysActiveUsers.toLocaleString()}
              </Typography>
            </Paper>
          ) : null}
        </Box>
      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          height: 600,
          width: "100%",
          maxWidth: "100%",
          overflow: "auto",
          pr: 2,
          "& .MuiDataGrid-root": {
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#756DE9 !important",
            color: "#fff !important",
            fontSize: "14px",
            fontWeight: "700",
            minHeight: "56px !important",
            maxHeight: "56px !important",
            lineHeight: "56px !important",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "700",
              color: "#fff !important",
              overflow: "visible",
              whiteSpace: "normal",
              lineHeight: "1.2",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#756DE9 !important",
            },
            "& .MuiDataGrid-iconSeparator": {
              color: "rgba(255, 255, 255, 0.5)",
            },
            "& .MuiDataGrid-sortIcon": {
              color: "#fff !important",
              opacity: 1,
            },
            "& .MuiDataGrid-menuIcon": {
              color: "#fff !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "#fff !important",
            },
          },
          "& .MuiDataGrid-cell": {
            fontSize: "14px",
            borderBottom: "1px solid #F0F0F0",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#F5F5FF",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #E0E0E0",
            backgroundColor: "#F8F8F8",
          },
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalRecords}
          paginationMode="server"
          loading={loading}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: "month_year", sort: "desc" }],
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography sx={{ color: "#999", fontSize: "16px" }}>
                  No data available
                </Typography>
              </Box>
            ),
            loadingOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <CircularProgress size={40} sx={{ color: "#756DE9" }} />
              </Box>
            ),
          }}
          sx={{
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
              background: "#F5F5F5",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              background: "#C1C1C1",
              borderRadius: "4px",
              "&:hover": {
                background: "#A8A8A8",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default MonthlyReport;

